import { useState, useEffect } from "react";
import { useAuthenticatedFetch } from "../Keycloak/useAuthenticatedFetch";
import { useAuth } from "../../context/AuthProvider";

export interface PowerMenuBlock {
    label: string;
    icon: string;
}

export interface PowerMenuBlocks {
    [key: string]: PowerMenuBlock;
}

export interface PowerMenuReportsLabels {
    [key: string]: string;
}

export interface PowerBiParamsItem {
    version: string;
    active: string;
    customer_visible: string;
    home: string;
}

export interface PowerBiParams {
    [workspace: string]: PowerBiParamsItem;
}

export interface PowerBiReportsWorkspace {
    [blockKey: string]: string[];
}

export interface PowerBiReports {
    [workspace: string]: PowerBiReportsWorkspace;
}

export interface PowerMenuData {
    blocks: PowerMenuBlocks;
    reportsLabels: PowerMenuReportsLabels;
    powerBiParams: PowerBiParams;
    powerBiReports: PowerBiReports;
}

export interface PowerBIMenuItem {
    id: string;
    name: string;
    url?: string;
    icon?: string;
    children?: PowerBIMenuItem[];
    [key: string]: string | number | boolean | undefined | PowerBIMenuItem[];
}

interface UseGetPowerMenuOptionsRaw {
    shouldFetch?: boolean;
    workspace: string;
    returnRaw: true;
}

interface UseGetPowerMenuOptionsData {
    shouldFetch?: boolean;
    workspace: string;
    returnRaw?: false;
}

type UseGetPowerMenuOptions = UseGetPowerMenuOptionsRaw | UseGetPowerMenuOptionsData;

// Plusieurs composants (sidebar, navbar horizontale, UserContext) montent ce hook
// en même temps pour le même workspace : ce cache partagé évite de déclencher
// 2-3 appels /getPowerBiMenu identiques en parallèle (voir contention réseau constatée).
const inFlightRequests = new Map<string, Promise<unknown>>();
const CACHE_TTL_MS = 60_000;
const responseCache = new Map<string, { data: unknown; expiresAt: number }>();

function useGetPowerMenu(options: UseGetPowerMenuOptionsRaw): {
    data: PowerBIMenuItem[] | null;
    loading: boolean;
    error: string;
    refetch: () => Promise<void>;
};

function useGetPowerMenu(options: UseGetPowerMenuOptionsData): {
    data: PowerMenuData | null;
    loading: boolean;
    error: string;
    refetch: () => Promise<void>;
};

function useGetPowerMenu(options: UseGetPowerMenuOptions) {
    const { shouldFetch = true, workspace, returnRaw = false } = options;
    const [data, setData] = useState<PowerMenuData | null>(null);
    const [rawData, setRawData] = useState<PowerBIMenuItem[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const authFetch = useAuthenticatedFetch();
    const { keycloakInstance, isLoading: authLoading } = useAuth();

    const fetchPowerMenu = async (): Promise<void> => {
        if (authLoading) {
            setLoading(true);
            return;
        }

        if (!keycloakInstance || !keycloakInstance.authenticated) {
            setError("User not authenticated");
            setLoading(false);
            return;
        }

        if (!workspace) {
            setError("No workspace provided");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");

        const cacheKey = `${workspace}_${returnRaw}`;

        try {
            const cached = responseCache.get(cacheKey);
            let parsedData: unknown;

            if (cached && cached.expiresAt > Date.now()) {
                parsedData = cached.data;
            } else {
                let pending = inFlightRequests.get(cacheKey);
                if (!pending) {
                    pending = authFetch(`/getPowerBiMenu`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                            "x-workspace": workspace,
                        },
                    }).then((result) => (typeof result === 'string' ? JSON.parse(result) : result))
                        .finally(() => inFlightRequests.delete(cacheKey));
                    inFlightRequests.set(cacheKey, pending);
                }
                parsedData = await pending;
                responseCache.set(cacheKey, { data: parsedData, expiresAt: Date.now() + CACHE_TTL_MS });
            }

            if (returnRaw) {
                setRawData(parsedData as PowerBIMenuItem[]);
            } else {
                setData(parsedData as PowerMenuData);
            }
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError("An unknown error occurred");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (shouldFetch && workspace && !authLoading && keycloakInstance?.authenticated) {
            fetchPowerMenu();
        }
    }, [shouldFetch, workspace, authLoading, keycloakInstance?.authenticated]);

    return {
        data: returnRaw ? rawData : data,
        loading,
        error,
        refetch: fetchPowerMenu
    } as any;
}

export default useGetPowerMenu;
